import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import { createUploadLink } from 'apollo-upload-client'
import { setContext } from '@apollo/client/link/context'
import { toaster, extractMsgFromGraphqlError } from './utils'
import { getCurrentLocalLcid } from './utils/lang'
import config from './config'

const QUIET_GQL_CODES = new Set([
  'UNAUTHENTICATED',
  'GRAPHQL_VALIDATION_FAILED',
  'BAD_USER_INPUT',
])

const shouldToastGraphqlError = (value) => {
  const code = value && value.extensions && value.extensions.code
  if (code && QUIET_GQL_CODES.has(code)) return false
  const msg = String((value && value.message) || '')
  // Tenant bootstrap / CORS / context noise — log only
  if (/Cannot find the tenant in context/i.test(msg)) return false
  if (/Not authorized/i.test(msg) && code === 'FORBIDDEN') return false
  return true
}

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    setTimeout(() => {
      graphQLErrors.forEach((value) => {
        // eslint-disable-next-line no-console
        console.log('[GraphQL error]: ', value)
        if (!shouldToastGraphqlError(value)) return

        const gqlErrors = extractMsgFromGraphqlError(value)
        console.log('gqlErrors in apolloClient.js', gqlErrors)

        if (gqlErrors && gqlErrors.length > 0) {
          gqlErrors.forEach((error) => toaster.error(`${error}`))
        } else if (value && value.message) {
          toaster.error(`${value.message}`)
        }
      })
    }, 100)
  }

  if (networkError) {
    // eslint-disable-next-line no-console
    console.log(`[Network error]: ${networkError}`)
    const msg = String(networkError.message || networkError)
    // Transient browser/CORS noise — do not stack red toasts on every poll
    if (/Failed to fetch|Load failed|NetworkError when attempting to fetch/i.test(msg)) {
      return
    }
    toaster.error(`[Network error]: ${networkError.message}`)
  }
})

const uploadLink = createUploadLink({
  uri: config.endpoint,
})

const headersMiddleware = setContext(({ headers = {} }) => ({
  headers: {
    ...headers,
    locale: getCurrentLocalLcid() || 'en-GB',
    authorization: localStorage.getItem('jwt')
      ? localStorage.getItem('jwt')
      // eslint-disable-next-line
      : document.cookie.replace(/(?:(?:^|.*;\s*)jwt\s*\=\s*([^;]*).*$)|^.*$/, "$1"),
    ...(document.cookie.replace(/(?:(?:^|.*;\s*)chatSession\s*=\s*([^;]*).*$)|^.*$/, "$1")
      ? { 'chat-session': document.cookie.replace(/(?:(?:^|.*;\s*)chatSession\s*=\s*([^;]*).*$)|^.*$/, "$1") }
      : {}
    ),
  },
}))

const client = new ApolloClient({
  link: ApolloLink.from([
    errorLink,
    headersMiddleware,
    uploadLink,
  ]),
  cache: new InMemoryCache(),
})

export default client
